import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, Calendar, Wrench, Activity } from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';
import { supabase } from '@/lib/supabase/client';

interface StalenessInfo {
  days_since: number | null;
  median_gap: number | null;
  stale_60d: boolean;
  off_cadence: boolean;
}

interface CoverageInfo {
  earliestDate: string | null;
  latestDate: string | null;
  total: number;
  minutes: number;
  agendas: number;
  other: number;
  extracted: number;
  chunked: number;
}

interface ExtractionInfo {
  attendees: number;
  motions: number;
  financial: number;
}

interface LastScrapeInfo {
  status: string;
  created_at: string;
  pages_crawled: number;
  artifacts_found: number;
  totalSkipped: number;
  engine: string;
  error: string | null;
}

interface SourceHealthModalProps {
  sourceId: string;
  sourceName: string;
  sourceUrl: string;
  open: boolean;
  onClose: () => void;
  staleness?: StalenessInfo;
}

export const SourceHealthModal: React.FC<SourceHealthModalProps> = ({
  sourceId,
  sourceName,
  sourceUrl,
  open,
  onClose,
  staleness,
}) => {
  const [coverage, setCoverage] = useState<CoverageInfo | null>(null);
  const [extraction, setExtraction] = useState<ExtractionInfo | null>(null);
  const [lastScrape, setLastScrape] = useState<LastScrapeInfo | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !sourceId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const [procRes, dateRes, scrapeRes, attCount, motCount, finCount] =
          await Promise.all([
            apiFetch(`/api/sources/${sourceId}/processing-stats`),
            // Date range: fetch with fetchAll to avoid 1000-row cap
            apiFetch(`/api/artifacts?source_id=${sourceId}&limit=50000`),
            apiFetch(`/api/scrape-jobs?source_id=${sourceId}`),
            // Use COUNT with artifact FK join to avoid 1000-row limit
            supabase
              .from('artifact_attendees')
              .select('*, artifacts!inner(source_id)', {
                count: 'exact',
                head: true,
              })
              .eq('artifacts.source_id', sourceId),
            supabase
              .from('motions')
              .select('*, artifacts!inner(source_id)', {
                count: 'exact',
                head: true,
              })
              .eq('artifacts.source_id', sourceId),
            supabase
              .from('financial_actions')
              .select('*, artifacts!inner(source_id)', {
                count: 'exact',
                head: true,
              })
              .eq('artifacts.source_id', sourceId),
          ]);
        if (cancelled) return;

        setExtraction({
          attendees: attCount?.count ?? 0,
          motions: motCount?.count ?? 0,
          financial: finCount?.count ?? 0,
        });

        // Processing stats
        if (procRes.ok) {
          const p = await procRes.json();
          setCoverage(
            (prev) =>
              ({
                ...(prev ?? ({} as CoverageInfo)),
                extracted: p.data?.extracted ?? 0,
                chunked: p.data?.chunked ?? 0,
                total: p.data?.total ?? 0,
              }) as CoverageInfo
          );
        }

        // Artifacts — date range + type breakdown
        if (dateRes.ok) {
          const a = await dateRes.json();
          const rows = (a.data ?? []) as Array<{
            meeting_date: string | null;
            meeting_type: string | null;
          }>;
          const dates = rows
            .map((r) => r.meeting_date)
            .filter((d): d is string => d !== null)
            .sort();
          const types: Record<string, number> = {};
          for (const r of rows) {
            const t = r.meeting_type ?? 'Other';
            types[t] = (types[t] ?? 0) + 1;
          }
          setCoverage({
            earliestDate: dates[0] ?? null,
            latestDate: dates[dates.length - 1] ?? null,
            total: rows.length,
            minutes: (types['Minutes'] ?? 0) + (types['Minutes '] ?? 0),
            agendas: (types['Agenda'] ?? 0) + (types['Agenda '] ?? 0),
            other:
              rows.length -
              ((types['Minutes'] ?? 0) +
                (types['Minutes '] ?? 0) +
                (types['Agenda'] ?? 0) +
                (types['Agenda '] ?? 0)),
            extracted: 0,
            chunked: 0,
          });
        }

        // Last scrape
        if (scrapeRes.ok) {
          const s = await scrapeRes.json();
          const jobs = (s.data ?? []) as Array<Record<string, unknown>>;
          if (jobs.length > 0) {
            const j = jobs[0];
            const stats = (j.stats ?? {}) as Record<string, unknown>;
            const skipped = (stats.skipped ?? {}) as Record<string, number>;
            let totalSkip = 0;
            for (const v of Object.values(skipped)) totalSkip += v ?? 0;
            setLastScrape({
              status: (j.status as string) ?? 'unknown',
              created_at: (j.created_at as string) ?? '',
              pages_crawled: (stats.pages_crawled as number) ?? 0,
              artifacts_found: (stats.artifacts_found as number) ?? 0,
              totalSkipped: totalSkip,
              engine:
                ((stats as Record<string, unknown>).engine as string) ??
                'fetch',
              error: (j.error as string) ?? null,
            });
          }
        }
      } catch {
        /* ignore */
      }
      if (!cancelled) setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, sourceId]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });

  const stalenessLabel = staleness
    ? staleness.stale_60d
      ? `${staleness.days_since}d stale`
      : staleness.off_cadence
        ? `Off cadence — ${staleness.days_since}d vs ${staleness.median_gap}d median`
        : 'Up to date'
    : 'Unknown';

  const stalenessColor = staleness?.stale_60d
    ? 'text-red-700 bg-red-50 border-red-200'
    : staleness?.off_cadence
      ? 'text-amber-700 bg-amber-50 border-amber-200'
      : 'text-green-700 bg-green-50 border-green-200';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-50 w-full max-w-lg max-h-[85vh] overflow-y-auto bg-white shadow-2xl rounded-lg mx-4">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-gray-900 truncate">
              {sourceName}
            </h2>
            <p className="text-xs text-gray-500 truncate">{sourceUrl}</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Activity className="w-5 h-5 animate-spin mr-2" />
            Loading source health…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* ── Staleness ── */}
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <AlertTriangle className="w-3.5 h-3.5" /> Staleness
              </h3>
              <div className={`rounded-lg border px-4 py-3 ${stalenessColor}`}>
                <p className="text-sm font-semibold">{stalenessLabel}</p>
                {staleness?.days_since != null && (
                  <p className={`text-xs mt-0.5 ${stalenessColor}`}>
                    {staleness.days_since}d since last meeting
                    {staleness.off_cadence && staleness.median_gap != null
                      ? ` — median gap is ${staleness.median_gap}d (>2×)`
                      : ''}
                  </p>
                )}
              </div>
            </section>

            {/* ── Content Coverage ── */}
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <Calendar className="w-3.5 h-3.5" /> Content Coverage
              </h3>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Date range</span>
                  <span className="text-gray-900 font-medium">
                    {coverage?.earliestDate && coverage?.latestDate
                      ? `${fmt(coverage.earliestDate)} — ${fmt(coverage.latestDate)}`
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Total documents</span>
                  <span className="text-gray-900 font-medium">
                    {coverage?.total ?? 0}
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">By type</span>
                  <span className="text-gray-900 font-medium">
                    {coverage?.minutes ?? 0} Minutes · {coverage?.agendas ?? 0}{' '}
                    Agendas
                    {coverage && coverage.other > 0
                      ? ` · ${coverage.other} Other`
                      : ''}
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Pipeline</span>
                  <span className="text-gray-900 font-medium">
                    {coverage?.extracted ?? 0} text ✓ · {coverage?.chunked ?? 0}{' '}
                    chunks
                  </span>
                </div>
              </div>
            </section>

            {/* ── Extraction Coverage ── */}
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <Wrench className="w-3.5 h-3.5" /> Extraction Coverage
              </h3>
              <div className="border border-gray-200 rounded-lg divide-y divide-gray-100">
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Attendees</span>
                  <span className="text-gray-900 font-medium">
                    {(extraction?.attendees ?? 0).toLocaleString()}{' '}
                    <span className="text-green-600 text-xs">✓</span>
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Motions</span>
                  <span className="text-gray-900 font-medium">
                    {(extraction?.motions ?? 0).toLocaleString()}{' '}
                    <span className="text-green-600 text-xs">✓</span>
                  </span>
                </div>
                <div className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="text-gray-500">Financial</span>
                  <span className="text-gray-900 font-medium">
                    {(extraction?.financial ?? 0).toLocaleString()}{' '}
                    {extraction && extraction.financial > 0 ? (
                      <span className="text-green-600 text-xs">✓</span>
                    ) : (
                      <span className="text-red-500 text-xs">
                        ✗ needs pipeline
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </section>

            {/* ── Last Scrape ── */}
            <section>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                <Activity className="w-3.5 h-3.5" /> Last Scrape
              </h3>
              <div className="border border-gray-200 rounded-lg">
                {lastScrape ? (
                  <div className="divide-y divide-gray-100">
                    <div className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="text-gray-500">Status</span>
                      <span className="text-gray-900 font-medium capitalize">
                        {lastScrape.status}
                        {lastScrape.error && (
                          <span className="text-red-500 text-xs ml-1">
                            · {lastScrape.error.slice(0, 60)}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="text-gray-500">Ran</span>
                      <span className="text-gray-900 font-medium">
                        {lastScrape.created_at
                          ? fmt(lastScrape.created_at)
                          : '—'}
                      </span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="text-gray-500">
                        Pages · Artifacts · Skipped
                      </span>
                      <span className="text-gray-900 font-medium">
                        {lastScrape.pages_crawled} ·{' '}
                        {lastScrape.artifacts_found} · {lastScrape.totalSkipped}
                      </span>
                    </div>
                    <div className="flex justify-between px-4 py-2.5 text-sm">
                      <span className="text-gray-500">Engine</span>
                      <span className="text-gray-900 font-medium">
                        {lastScrape.engine || 'fetch'}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="px-4 py-3 text-sm text-gray-400">
                    No scrapes recorded
                  </p>
                )}
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};
